/**
 * ProposeChoreSheet — "Foreslå oppgave" for kids (Fase 3).
 *
 * A <BottomSheet> form letting any signed-in member suggest a chore. The fields
 * mirror the server's ProposeBody (title, description, icon, points, recurrence)
 * — the remaining parent-controlled fields (min_age, proof, assignment) are set
 * by a parent when they approve the suggestion in the Approvals tab.
 *
 * Utseende (#154, steg 4c, Stell_Foresla-skissen). Arket forblir et høyt ark
 * (D9): tittel og X ligger i arkets topp, «Send forslaget» nederst.
 *   - Feltene kommer i skissens rekkefølge: tittel, ikon, poeng, hvor ofte,
 *     beskrivelse.
 *   - Ikonene er et rutenett med seks synlige og «Flere» for resten.
 *   - Poengene er en Stepper (D12) med dagens grenser og standard: minst 1,
 *     ingen øvre grense, 10 som start. Steget er 1, så alle verdier som
 *     tallfeltet tillot, fortsatt kan velges; skissen bruker 5.
 *   - Gjentakelsen er RecurrencePicker som chips (D12). Månedlig ligger under
 *     «Utvalgte dager».
 *
 * On a successful propose the sheet closes; useProposeChore surfaces the warm
 * "sent til en voksen" toast.
 *
 * On a failed propose the sheet STAYS open, so the error is rendered inline
 * above the button rather than toasted: ToastViewport is z-40 and the
 * sheet's overlay is z-50, so a toast fired from here hides behind the scrim
 * (#74).
 */
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { Recurrence } from '@stell/shared';
import {
  BottomSheet,
  Button,
  choreGlyph,
  Icon,
  SheetError,
  Stepper,
} from '../../components/primitives';
import { RecurrencePicker } from '../admin/RecurrencePicker';
import { SectionLabel } from '../../components/primitives/SectionLabel';
import { useProposeChore } from './useProposeChore';

/**
 * Ikonene i rutenettet. De seks første står synlige (skissens rad: oppvask,
 * vanne, rydde, søppel, lese — og stjerna, som er standardvalget); resten
 * kommer fram bak «Flere».
 */
const ICON_KEYS = [
  'sparkle', 'dishes', 'plant', 'vacuum', 'trash', 'book',
  'bed', 'laundry', 'dog', 'bulb', 'flame', 'therm', 'check',
] as const;
const VISIBLE_ICONS = 6;

const DEFAULT_ICON = 'sparkle';
const DEFAULT_POINTS = 10;
const DEFAULT_RECURRENCE: Recurrence = { type: 'daily' };

export interface ProposeChoreSheetProps {
  open: boolean;
  onClose: () => void;
}

export function ProposeChoreSheet({ open, onClose }: ProposeChoreSheetProps): ReactElement {
  const { t } = useTranslation();
  const propose = useProposeChore();

  const [title, setTitle] = useState('');
  const [iconKey, setIconKey] = useState<string>(DEFAULT_ICON);
  const [allIcons, setAllIcons] = useState(false);
  const [description, setDescription] = useState('');
  const [points, setPoints] = useState(DEFAULT_POINTS);
  const [recurrence, setRecurrence] = useState<Recurrence>(DEFAULT_RECURRENCE);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // «Flere» forsvinner når den er trykket, så fokus flyttes til det første
  // ikonet som kom fram, i stedet for å falle ut av arket.
  const firstMoreIconRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (allIcons) firstMoreIconRef.current?.focus();
  }, [allIcons]);

  // Clear a stale error when the sheet reopens (mirrors ChoreCompleteSheet).
  useEffect(() => {
    if (open) return;
    setSubmitError(null);
  }, [open]);

  const valid = title.trim().length > 0 && points >= 1;

  function reset(): void {
    setTitle('');
    setIconKey(DEFAULT_ICON);
    setAllIcons(false);
    setDescription('');
    setPoints(DEFAULT_POINTS);
    setRecurrence(DEFAULT_RECURRENCE);
  }

  function handleSubmit(): void {
    if (!valid || propose.isPending) return;
    setSubmitError(null);
    propose.mutate(
      {
        title: title.trim(),
        icon_key: iconKey,
        point_value: points,
        recurrence,
        ...(description.trim() ? { description: description.trim() } : {}),
      },
      {
        onSuccess: () => {
          reset();
          onClose();
        },
        // The hook deliberately does not toast the error (#74); this sheet owns it.
        onError: () => setSubmitError(t('chores.propose.error')),
      },
    );
  }

  const shownIcons = allIcons ? ICON_KEYS : ICON_KEYS.slice(0, VISIBLE_ICONS);

  return (
    <BottomSheet open={open} onClose={onClose} title={t('chores.propose.title')} snapPoints="full">
      <form
        data-testid="propose-chore-form"
        className="flex flex-col gap-4.5"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <div className="flex flex-col">
          <SectionLabel as="label" htmlFor="propose-title">
            {t('chores.propose.field.title')}
          </SectionLabel>
          <input
            id="propose-title"
            data-testid="propose-title"
            className="field"
            type="text"
            maxLength={120}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div
          role="group"
          aria-labelledby="propose-icon-label"
          className="flex flex-col"
          data-testid="propose-icon-picker"
        >
          <SectionLabel as="span" id="propose-icon-label">
            {t('admin.choreTemplates.field.icon')}
          </SectionLabel>
          <div className="flex flex-wrap gap-2">
            {shownIcons.map((key, i) => {
              const selected = iconKey === key;
              const Glyph = Icon[choreGlyph(key)];
              return (
                <button
                  key={key}
                  ref={i === VISIBLE_ICONS ? firstMoreIconRef : undefined}
                  type="button"
                  data-testid={`propose-icon-${key}`}
                  aria-pressed={selected}
                  onClick={() => setIconKey(key)}
                  className={[
                    'flex h-12 w-11 items-center justify-center rounded-md',
                    selected
                      ? 'bg-fab text-on-fab'
                      : 'border border-secondary-line bg-secondary text-ink',
                  ].join(' ')}
                >
                  <Glyph size={22} />
                  <span className="sr-only">{t(`admin.choreTemplates.iconLabel.${key}`)}</span>
                </button>
              );
            })}
            {allIcons ? null : (
              <button
                type="button"
                data-testid="propose-icon-more"
                aria-expanded={false}
                onClick={() => setAllIcons(true)}
                className="h-12 rounded-pill border border-secondary-line bg-secondary px-3.5 text-sm font-bold text-ink"
              >
                {t('chores.propose.moreIcons')}
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-col">
          <SectionLabel as="span">
            {t('admin.choreTemplates.field.points')}
          </SectionLabel>
          <div className="flex items-center gap-3.5">
            <Stepper
              data-testid="propose-points"
              label={t('admin.choreTemplates.field.points')}
              value={points}
              min={1}
              suffix=" p"
              decreaseLabel={t('chores.propose.pointsDecrease')}
              increaseLabel={t('chores.propose.pointsIncrease')}
              onChange={setPoints}
            />
            <span className="text-caption text-ink-2">{t('chores.propose.pointsHint')}</span>
          </div>
        </div>

        <RecurrencePicker
          variant="chips"
          label={t('chores.propose.field.recurrence')}
          value={recurrence}
          onChange={setRecurrence}
        />

        <div className="flex flex-col">
          <SectionLabel as="label" htmlFor="propose-description">
            {t('chores.propose.field.description')}
          </SectionLabel>
          <textarea
            id="propose-description"
            data-testid="propose-description"
            className="field resize-none leading-snug"
            maxLength={2000}
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {/* Submit error — inline, never a toast (#74). */}
        <SheetError message={submitError} data-testid="propose-submit-error" />

        <div className="flex flex-col items-center gap-2">
          <Button
            type="submit"
            size={56}
            className="w-full"
            disabled={!valid || propose.isPending}
            data-testid="propose-submit"
          >
            {t('chores.propose.submit')}
          </Button>
          <span className="text-caption text-ink-2">{t('chores.propose.intro')}</span>
        </div>
      </form>
    </BottomSheet>
  );
}
